/**
 * The sample export pair, for someone meeting the tool with no files of their own.
 *
 * The two real Bellingham exports the engine is tested against, served from
 * `<base>samples/` by the `serve-sample-exports` plugin in `vite.config.ts`.
 * Using the real pair rather than a toy is the point: it carries the annual
 * re-bid duplicate titles, the document payloads and the churn that every
 * explanation in the README refers to, so the tour shows the tool answering a
 * real question rather than a staged one.
 *
 * Kept out of the JS bundle deliberately — 2.9 MB fetched on demand by someone
 * who asked for it, never shipped to someone who did not.
 */

/** Names as the plugin serves them, and as they appear in the run's metadata. */
export const SAMPLE_BASELINE_FILE = "bellingham-reference.json";
export const SAMPLE_LATEST_FILE = "bellingham-candidate.json";

/** What the sample pair is, for the button's own explanation. */
export const SAMPLE_DESCRIPTION =
  "Two real runs of the Bellingham ProcureWare source, a week apart — the pair the engine is tested against.";

export type SamplePair = { baseline: File; latest: File };

/**
 * Fetch the sample pair as `File`s, indistinguishable downstream from a pair
 * the person picked themselves.
 *
 * `base` and `fetchImpl` are injectable so this is testable without a server;
 * in the app they default to the deployed base path and the platform fetch.
 *
 * A failure is thrown with a plain reason rather than swallowed: a button that
 * silently does nothing is the worst outcome for the person this exists for.
 */
export async function loadSamplePair(
  options: { base?: string; fetchImpl?: typeof fetch } = {}
): Promise<SamplePair> {
  const base = options.base ?? import.meta.env.BASE_URL;
  const fetchImpl = options.fetchImpl ?? fetch;

  const [baseline, latest] = await Promise.all([
    fetchSample(SAMPLE_BASELINE_FILE, base, fetchImpl),
    fetchSample(SAMPLE_LATEST_FILE, base, fetchImpl)
  ]);
  return { baseline, latest };
}

async function fetchSample(name: string, base: string, fetchImpl: typeof fetch): Promise<File> {
  const url = `${base.endsWith("/") ? base : `${base}/`}samples/${name}`;
  let response: Response;
  try {
    response = await fetchImpl(url);
  } catch {
    // Offline, or the deployment is missing the samples directory.
    throw new Error(`The sample data could not be fetched (${name}). Check your connection and try again.`);
  }
  if (!response.ok) {
    throw new Error(`The sample data could not be loaded (${name}: HTTP ${response.status}).`);
  }
  // Bytes, not text: `Response.text()` decodes UTF-8 and drops the BOM, which
  // would hand the app a sample three bytes shorter than the file a person
  // actually downloads. The exports carry a BOM, the loader is built to strip
  // one, and a sample that quietly lacks it would exercise a path the real
  // files never take.
  const bytes = await response.arrayBuffer();
  return new File([bytes], name, { type: "application/json" });
}
